import {
  Route,
  Routes,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  useParams,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { NotFound } from '@common/components/NotFound/NotFound'
import { ROUTES } from './routes'
import { Toaster } from 'sonner'
import setAxiosConfig from '@common/config/api/setAxiosConfig'
import CreateOrUpdateStudyLayout from '@study_setup/components/layouts/CreateOrUpdateStudyLayout'
import CreateOrUpdateStudy from '@study_setup/components/CreateOrUpdateStudy'
import ReaderTaskListTabs from './features/reader_task_list_view/pages/ReaderTaskListTabs'
import { FormManagement } from './features/form_management/components'
import StudyDetailsLayout from '@study_setup/components/layouts/StudyDetailsLayout'
import QcTaskListTabs from '@qc_task_list_view/pages/QcTaskListTabs'
import { BaseLayout } from '@common/components/layouts/BaseLayout'
import { AccountManagement, Queries, AccountManagementProfile, StudyDetails } from '@features/study_details/components'
import { ProtectedRoute } from '@auth/components/ProtectedRoute'
import {
  FormManagementPermission,
  SeriesLabelManagementPermission,
  StudyPermission,
  CompanyManagementPermission,
  UserManagementPermission,
  SubjectPermission,
  QueryPermission,
  QCTaskPermission,
  ReadTaskPermission,
} from '@auth/permissionsEnum'
import Forbidden from '@auth/components/Forbidden'
import Home from '@features/study_view/pages'
import SeriesLabelManagement from '@features/series_label_management/components/SeriesLabelManagement'
import { UserManagement, UserDetails } from '@features/user_management'
import Sites from '@features/study_details/sites/components'
import ReaderTaskView from '@reader_task_list_view/pages/ReaderTaskView'
import SubjectsTimepoints from '@features/subject/pages/SubjectsTimepoints'
import Subjects from '@features/study_details/Subjects/components'
import { CombinedPermissionsProvider } from '@auth/hooks/useCombinedPermissions'
import Reporting from '@features/study_details/Reports/Reporting'
import CompanyManagement from '@features/company_management/components/CompanyManagement'
import ProfileSettings from '@features/user_management/details/components/ProfileSettings'
import { TagDeidManagement } from '@features/tag_deid_management'
import SubjectDetailsLayout from '@features/subject/layouts/SubjectDetailsLayout'
import SubjectOverview from '@features/subject/components/SubjectDetails/SubjectOverview'
import SubjectTimepointSubmission from '@features/subject/components/SubjectDetails/SubjectTimepointSubmission'
import SubjectQueries from '@features/subject/components/SubjectDetails/SubjectQueries'
import QCOverview from '@features/qc_task_details_view/pages/qc/QCOverview'
import QCQueries from '@features/qc_task_details_view/pages/qc/QCQueries'
import QCDetailsLayout from '@features/qc_task_details_view/pages/qc/QCDetailsLayout'
import SubjectQCTasks from '@features/subject/components/SubjectDetails/SubjectQCTasks'
import SubjectReadTasks from '@features/subject/components/SubjectDetails/SubjectReadTasks'
import useIdleLogout from '@auth/hooks/useIdleLogout'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [/^\//],
  profilesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

setAxiosConfig()

if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/uppySw.js')
    .then((registration) => {
      console.log('ServiceWorker registration successful with scope: ', registration.scope)
    })
    .catch((error) => {
      console.log(`Registration failed with ${error}`)
    })
}

export default function App() {
  const { studyId } = useParams<{ studyId: string }>()

  useIdleLogout(15) // 15 minutes

  return (
    <>
      <Toaster
        closeButton
        position="top-right"
        style={{
          marginTop: '16px',
          marginRight: '16px',
        }}
      />
      <SentryRoutes>
        <Route element={<BaseLayout />}>
          <Route path={ROUTES.HOME.path} element={<Home />} />
          <Route path={ROUTES.NOT_FOUND.path} element={<NotFound />} />
          <Route path={ROUTES.FORBIDDEN.path} element={<Forbidden />} />
          <Route
            path={ROUTES.USERS_MANAGEMENT.path}
            element={
              <ProtectedRoute permissions={[UserManagementPermission.ALL, UserManagementPermission.LIST_USERS]}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.USER_PROFILE.path}
            element={
              <ProtectedRoute permissions={[UserManagementPermission.ALL, UserManagementPermission.VIEW_USER]}>
                <UserDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.FORM_MANAGEMENT.path}
            element={
              <ProtectedRoute permissions={[FormManagementPermission.ALL]}>
                <FormManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SERIES_LABEL_MANAGEMENT.path}
            element={
              <ProtectedRoute permissions={[SeriesLabelManagementPermission.ALL]}>
                <SeriesLabelManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.COMPANIES_MANAGEMENT.path}
            element={
              <ProtectedRoute
                permissions={[CompanyManagementPermission.ALL, CompanyManagementPermission.LIST_ALL_COMPANIES]}
              >
                <CompanyManagement />
              </ProtectedRoute>
            }
          />
          <Route path={ROUTES.PROFILE_SETTINGS.path} element={<ProfileSettings />} />
          <Route path={ROUTES.TAG_DEID_MANAGEMENT.path} element={<TagDeidManagement />} />
        </Route>

        <Route element={<StudyDetailsLayout />}>
          <Route
            path={ROUTES.STUDY_DETAILS.path}
            element={
              <ProtectedRoute permissions={[]}>
                <StudyDetails />
              </ProtectedRoute>
            }
          />
          <Route path={ROUTES.SITES.path} element={<Sites />} />
          <Route path={ROUTES.READER_TASKS.path} element={<ReaderTaskView />} />
          <Route path={ROUTES.SUBJECTS.path} element={<Subjects />} />
          <Route path={ROUTES.SUBJECTS_TIMEPOINTS.path} element={<SubjectsTimepoints />} />
          <Route path={ROUTES.READER_TASKS.path} element={<ReaderTaskListTabs />} />
          <Route path={ROUTES.QC_TASKS.path} element={<QcTaskListTabs />} />
          <Route path={ROUTES.QUERIES.path} element={<Queries />} />
          <Route path={ROUTES.QUERY_DETAILS.path} element={<Queries />} />
          <Route
            path={ROUTES.ACCOUNT_MANAGEMENT.path}
            element={
              <CombinedPermissionsProvider studyId={studyId!}>
                <AccountManagement />
              </CombinedPermissionsProvider>
            }
          />
          <Route
            path={ROUTES.ACCOUNT_MANAGEMENT_PROFILE.path}
            element={
              <CombinedPermissionsProvider studyId={studyId!}>
                <AccountManagementProfile />
              </CombinedPermissionsProvider>
            }
          />
          <Route
            path={ROUTES.STUDY_REPORTS.path}
            element={
              <CombinedPermissionsProvider studyId={studyId!}>
                <Reporting />
              </CombinedPermissionsProvider>
            }
          />
        </Route>

        <Route element={<SubjectDetailsLayout />}>
          <Route
            path={ROUTES.SUBJECT_OVERVIEW.path}
            element={
              <ProtectedRoute
                permissions={[SubjectPermission.ALL, SubjectPermission.VIEW, SubjectPermission.VIEW_FOR_OWN_SITE]}
              >
                <SubjectOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUBJECT_TIMEPOINTS.path}
            element={
              <ProtectedRoute
                permissions={[SubjectPermission.ALL, SubjectPermission.VIEW, SubjectPermission.VIEW_FOR_OWN_SITE]}
              >
                <SubjectTimepointSubmission />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUBJECT_TIMEPOINT_SUBMISSION.path}
            element={
              <ProtectedRoute
                permissions={[SubjectPermission.ALL, SubjectPermission.VIEW, SubjectPermission.VIEW_FOR_OWN_SITE]}
              >
                <SubjectTimepointSubmission />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUBJECT_QC_TASKS.path}
            element={
              <ProtectedRoute
                permissions={[
                  QCTaskPermission.ALL,
                  QCTaskPermission.VIEW_TASK_POOL,
                  QCTaskPermission.VIEW_ASSIGNED_TASKS,
                  QCTaskPermission.VIEW_COMPLETED_TASKS,
                  QCTaskPermission.MANAGE_TASKS,
                ]}
              >
                <SubjectQCTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUBJECT_READ_TASKS.path}
            element={
              <ProtectedRoute
                permissions={[
                  ReadTaskPermission.ALL,
                  ReadTaskPermission.VIEW_ASSIGNED_TASKS,
                  ReadTaskPermission.VIEW_TASK_POOL,
                  ReadTaskPermission.VIEW_COMPLETED_TASKS,
                  ReadTaskPermission.MANAGE_READ_TASK,
                ]}
              >
                <SubjectReadTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SUBJECT_QUERIES.path}
            element={
              <ProtectedRoute
                permissions={[QueryPermission.ALL, QueryPermission.LIST_ALL, QueryPermission.LIST_FOR_OWN_SITE]}
              >
                <SubjectQueries />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route element={<QCDetailsLayout />}>
          <Route
            path={ROUTES.QC_TASK_OVERVIEW.path}
            element={
              <ProtectedRoute
                permissions={[
                  QCTaskPermission.ALL,
                  QCTaskPermission.VIEW_ASSIGNED_TASKS,
                  QCTaskPermission.MANAGE_TASKS,
                  QCTaskPermission.AUDIT,
                ]}
              >
                <QCOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.QC_TASK_QUERIES.path}
            element={
              <ProtectedRoute
                permissions={[QueryPermission.ALL, QueryPermission.LIST_ALL, QueryPermission.LIST_FOR_OWN_SITE]}
              >
                <QCQueries />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route element={<CreateOrUpdateStudyLayout />}>
          <Route
            path={ROUTES.CREATE_STUDY.path}
            element={
              <ProtectedRoute permissions={[StudyPermission.ALL]}>
                <CreateOrUpdateStudy />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.UPDATE_STUDY.path}
            element={
              <ProtectedRoute permissions={[StudyPermission.ALL]}>
                <CreateOrUpdateStudy />
              </ProtectedRoute>
            }
          />
        </Route>
      </SentryRoutes>
    </>
  )
}
